/* ------------------------------------------------------------------ */
/* Seção 4 — APIs Comerciais (alternativa Buy vs Build)                 */
/* ------------------------------------------------------------------ */
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';

interface APIComercial {
  nome: string;
  site: string;
  modelo: string;
  preco: string;
  oQueEntrega: string[];
  usoPratico: string[];
  limitacoes: string[];
  quandoFazSentido: string;
}

const apisComerciais: APIComercial[] = [
  {
    nome: 'JUDIT',
    site: 'https://judit.io',
    modelo: 'SaaS — consulta por crédito/mês',
    preco: 'R$ 0,39/consulta (básica) a R$ 0,60+ (com autos). Planos mensais disponíveis.',
    oQueEntrega: [
      'Consulta de processos por número CNJ, OAB ou CPF/CNPJ',
      'Movimentações e autos de processos em múltiplos tribunais',
      'Cobertura ampla — mais tribunais do que DataJud para autos completos',
      'API REST com documentação, sem precisar gerenciar scraping próprio',
      'Notificação de movimentação nova por webhook',
    ],
    usoPratico: [
      'Aba "Andamentos" do processo (D02) — fallback para tribunais sem adapter próprio, badge "via JUDIT (premium)"',
      'Descoberta de processos por OAB / CPF / CNPJ — alimenta D02 e D01',
      'Peças em PDF armazenadas em F06 Storage (planos com autos)',
      'Controlado por feature flag por tenant — não disponível no plano base',
    ],
    limitacoes: [
      'Custo por consulta — escala mal para monitoramento contínuo de muitos processos',
      'Dependência de fornecedor externo — se o JUDIT mudar preço ou parar, impacto imediato',
      'Dados passam por terceiro — implicações de privacidade/LGPD a avaliar',
      'Cobertura varia por tribunal — verificar se cobre todos os tribunais do cliente',
    ],
    quandoFazSentido:
      'Quando o volume de consultas é baixo (escritórios de até 20-30 processos ativos) e o custo de desenvolvimento do adapter próprio não se paga. Ou como ponte temporária enquanto J05 não está pronto. Calculadora: se custo mensal JUDIT > salário de 1 dev por mês, build próprio ganha.',
  },
  {
    nome: 'Escavador',
    site: 'https://www.escavador.com',
    modelo: 'SaaS — API para monitoramento processual',
    preco: 'Planos a partir de R$ 297/mês (pequeno escritório). API corporativa sob cotação.',
    oQueEntrega: [
      'Monitoramento de processos por OAB, CPF/CNPJ ou número CNJ',
      'Alertas de movimentação por email/webhook',
      'Pesquisa de diários da justiça (DJE) de múltiplos tribunais',
      'Informações societárias e de advogados vinculados a processos',
    ],
    usoPratico: [
      'Aba "Andamentos" do processo (D02) como fallback — badge "via Escavador (premium)"',
      'Descoberta de processos por OAB / CPF / CNPJ — alimenta D02 e D01',
      'Opção para adiar J03 (DJE) no roadmap: Escavador já cobre publicações de DJE',
      'Controlado por feature flag por tenant',
    ],
    limitacoes: [
      'Modelo de dados proprietário — lock-in de fornecedor',
      'Custo recorrente significativo para volumes maiores',
      'Sem acesso a autos completos (apenas movimentações e publicações)',
      'API corporativa sem preço público — cotação necessária para volumes maiores',
    ],
    quandoFazSentido:
      'Quando monitoramento de DJE e alertas de publicação são a prioridade e o cliente não precisa de autos completos. Complementar ao DataJud (que não cobre DJE). Opção para adiar J03 (DJE) no roadmap.',
  },
  {
    nome: 'Infosimples',
    site: 'https://www.infosimples.com',
    modelo: 'SaaS — consultas a bases públicas, incluindo processos judiciais',
    preco: 'Modelo por crédito — preços variam por tribunal e profundidade da consulta.',
    oQueEntrega: [
      'Consultas a portais públicos de múltiplos tribunais via API',
      'Cobre tribunais que nem o DataJud nem o JUDIT cobrem bem',
      'Login + password + pkcs12 cert para tribunais que exigem auth (Issue #615 na comunidade)',
      'Consultas a outras bases (Receita Federal, DETRAN, etc.) — útil para CRM jurídico',
    ],
    usoPratico: [
      'Aba "Andamentos" do processo (D02) como fallback — badge "via Infosimples (premium)"',
      'Descoberta de processos por OAB / CPF / CNPJ — alimenta D02 e D01',
      'Uso para consultas pontuais em tribunais não cobertos, não monitoramento contínuo',
      'Controlado por feature flag por tenant',
    ],
    limitacoes: [
      'Mesmo trade-off do JUDIT: custo por consulta, lock-in de fornecedor',
      'Qualidade da cobertura varia por tribunal',
      'Documentação menos madura que o JUDIT',
    ],
    quandoFazSentido:
      'Quando um cliente específico tem processos em tribunais que o VoxisAdv ainda não cobre com adapter próprio. Consultas pontuais, não monitoramento contínuo.',
  },
];

function APICard({ api }: { api: APIComercial }) {
  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">
              <a
                href={api.site}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[var(--accent)] hover:underline"
              >
                {api.nome}
              </a>
            </CardTitle>
            <CardDescription className="mt-0.5 text-xs">{api.modelo}</CardDescription>
          </div>
          <span className="rounded bg-[var(--surface-muted)] px-2 py-1 text-xs text-[var(--fg-muted)] whitespace-nowrap">
            {api.preco}
          </span>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-green-400">
              O que entrega
            </p>
            <ul className="space-y-1.5">
              {api.oQueEntrega.map((txt, i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                  <span className="mt-0.5 shrink-0 text-green-400">•</span>
                  {txt}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-red-400">
              Limitações
            </p>
            <ul className="space-y-1.5">
              {api.limitacoes.map((txt, i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                  <span className="mt-0.5 shrink-0 text-red-400">•</span>
                  {txt}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {api.usoPratico.length > 0 && (
          <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3">
            <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-blue-400">
              <span>◈</span> Uso prático no app
            </p>
            <ul className="space-y-1.5">
              {api.usoPratico.map((txt, i) => (
                <li key={i} className="flex gap-2 text-sm text-[var(--fg)]">
                  <span className="mt-0.5 shrink-0 text-blue-400">•</span>
                  {txt}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="rounded-lg bg-[var(--surface-muted)]/60 p-3">
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--accent)]">
            Quando faz sentido comprar
          </p>
          <p className="text-sm text-[var(--fg-muted)] leading-relaxed">{api.quandoFazSentido}</p>
        </div>
      </CardContent>
    </Card>
  );
}

export function SecaoAPIsComerciais() {
  return (
    <section aria-label="APIs comerciais alternativas">
      <div className="mb-6 space-y-2">
        <div className="flex items-center gap-2">
          <span className="rounded bg-[var(--surface-muted)] px-2 py-0.5 font-mono text-xs font-bold text-[var(--fg-muted)]">
            SEÇÃO 4
          </span>
          <h2 className="text-xl font-bold tracking-tight">APIs Comerciais — Buy vs Build</h2>
        </div>
        <p className="max-w-2xl text-sm text-[var(--fg-muted)] leading-relaxed">
          Alternativas prontas para quando o custo de desenvolver adapter próprio não se justifica. Úteis como ponte temporária ou para tribunais de baixo volume. Calcule: custo mensal da API vs custo de desenvolver e manter o adapter.
        </p>
      </div>

      <div className="space-y-4">
        {apisComerciais.map((api) => (
          <APICard key={api.nome} api={api} />
        ))}
      </div>
    </section>
  );
}
